import axios, { AxiosPromise, AxiosRequestConfig } from 'axios'
import qs from 'qs'

import { getAuthCookie, removeAuthCookie } from './cookies'
import { getEnvironment } from './environment'
import { getSignInUrl } from './auth'

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  domain?: string
  environment?: string
  onboardingService?: Boolean
}

export const setupAxios = () => {
  axios.interceptors.request.use((config: any) => {
    const { domain, environment, onboardingService } = config

    const { token } = getAuthCookie()
    const env = environment || getEnvironment()

    const url = onboardingService ? getOnboardingUrl(env) : buildTenantUrl({ domain, env })

    config.url = `${url}${config.url}`

    if (!config.headers.authorization && token) {
      config.headers.authorization = token
    }

    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json'
    }

    if (config.data && typeof config.data === 'object') {
      config.data = JSON.stringify(config.data)
    }

    config.timeout = 200000

    config.paramsSerializer = paramsSerializer

    return config
  })

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data.error_message !== 'invalid-login-or-password'
      ) {
        removeAuthCookie()
        window.location.href = getSignInUrl()
      }

      return Promise.reject(error.response.data)
    },
  )
}

const partialUrlByEnvironment = {
  au: '',
  local: '.api.local',
  qa: '.qa',
  demo: '.demo',
}

const buildTenantUrl = ({ domain, env }: { domain: string; env: string }) => {
  const environment = (env || 'au').toLowerCase().trim()

  const environmentPrefix = partialUrlByEnvironment[environment]

  return getTenantUrl(domain || 'global', environmentPrefix)
}

const getTenantUrl = (tenant: string, environment: string) =>
  '%protocol%://%tenant%%environment%.imperium.markets/api'
    .replace('%protocol%', String(environment).includes('local') ? 'http' : 'https')
    .replace('%tenant%', tenant)
    .replace('%environment%', environment)

const getOnboardingUrl = (environment: string) => {
  const env = (environment || 'au').toLowerCase().trim()
  const environmentPrefix = env === 'local' ? '.local' : partialUrlByEnvironment[env]

  return '%protocol%://onboarding%environment%.imperium.markets/api'
    .replace('%protocol%', String(environmentPrefix).includes('local') ? 'http' : 'https')
    .replace('%environment%', environmentPrefix)
}

const paramsSerializer = (params) => qs.stringify(params, { arrayFormat: 'brackets' })

export const httpClient = (url: string, options?: CustomAxiosRequestConfig): AxiosPromise<any> => axios(url, options)
